// event-taxonomy.ts — Single source of truth (TS side) for event taxonomy,
// domain mapping, and UI labels/colours.
//
// Mirror kept in sync manually with pipeline/scripts/event_taxonomy.py.
// When you change EVENT_TYPES / EVENT_DOMAIN, update the Python source AND
// bump extractor_version in the pipeline. A parity test asserts both lists match.

export const EVENT_TYPES = [
  "thien-tai",
  "dich-benh",
  "tai-nan",
  "chay-no",
  "o-nhiem",
  "dau-tu-cong",
  "fdi-du-an",
  "chinh-sach-ban-hanh",
  "bien-dong-gia",
  "dinh-cong",
  "an-ninh",
  "quy-hoach",
  "khac",
] as const;

export type EventType = (typeof EVENT_TYPES)[number];

export const DOMAINS = ["economy", "policy", "social", "hazard"] as const;
export type Domain = (typeof DOMAINS)[number];

export const EVENT_DOMAIN: Record<EventType, Domain> = {
  "thien-tai": "hazard",
  "chay-no": "hazard",
  "o-nhiem": "hazard",
  "dau-tu-cong": "economy",
  "fdi-du-an": "economy",
  "bien-dong-gia": "economy",
  "chinh-sach-ban-hanh": "policy",
  "quy-hoach": "policy",
  "dich-benh": "social",
  "tai-nan": "social",
  "dinh-cong": "social",
  "an-ninh": "social",
  khac: "social",
};

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  "thien-tai": "Thiên tai",
  "dich-benh": "Dịch bệnh",
  "tai-nan": "Tai nạn",
  "chay-no": "Cháy nổ",
  "o-nhiem": "Ô nhiễm",
  "dau-tu-cong": "Đầu tư công",
  "fdi-du-an": "Dự án FDI",
  "chinh-sach-ban-hanh": "Chính sách ban hành",
  "bien-dong-gia": "Biến động giá",
  "dinh-cong": "Đình công",
  "an-ninh": "An ninh",
  "quy-hoach": "Quy hoạch",
  khac: "Khác",
};

export const DOMAIN_LABELS: Record<Domain, string> = {
  economy: "Kinh tế",
  policy: "Chính sách",
  social: "Xã hội",
  hazard: "Thiên tai / Sự cố",
};

// Domain colours for heatmap/badges (theme-neutral hues).
export const DOMAIN_COLORS: Record<Domain, string> = {
  economy: "#2563eb",
  policy: "#7c3aed",
  social: "#0891b2",
  hazard: "#dc2626",
};

const EVENT_TYPE_SET = new Set<string>(EVENT_TYPES);
const DOMAIN_SET = new Set<string>(DOMAINS);

export function isEventType(v: string): v is EventType {
  return EVENT_TYPE_SET.has(v);
}

export function isDomain(v: string): v is Domain {
  return DOMAIN_SET.has(v);
}

// Server-side helper for domain -> event_type IN clause (events.ts filter).
export function eventTypesForDomain(domain: Domain): EventType[] {
  return EVENT_TYPES.filter((t) => EVENT_DOMAIN[t] === domain);
}
